import * as React from "react";
import { styled, Theme, CSSObject, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import logoRbr from "../../img/logo/logo-rbr.png";
import logoEnergosbyt from "../../img/logo/logo-energosbyt.png";
import { Link, useLocation } from "react-router-dom";
import { AppRoute, LocalStorageMethod, UserRole } from "../../enums/enum";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { useEffect } from "react";
import { getMenuState } from "../../store/main-process/selector";
import { showMenu } from "../../store/main-process/main-process";
import { getUserInfo } from "../../store/user-process/selector";
import LocalStorageProvider from "../../utils/locaStorageProvider";
import LicDialog from "../dialog/lic/LicDialog";
import ChangeLick from "../dialog/lic/changeLic";
import { GetFullInfoByLic, GetPeriodStatus, GetUserLicAddresses } from "../../services/api-actions/integration-action";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import authService from "../api-authorization/AuthorizeService";
import useWindowDimensions from "../../Hooks/windowDimensions";
import { getPersonalInfo } from "../../store/integration-process/selector";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import PdfViewerDialog, { POLICY_DOWNLOAD_NAME, POLICY_PDF_URL, POLICY_TITLE } from "../dialog/pdf-viewer/PdfViewerDialog";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { userIsRole } from "../../utils/user-is-role";
import userDisplayName from "../../utils/userDisplayName";
import { brand } from "../../theme";

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  [theme.breakpoints.up("xs")]: {
    width: 0,
  },
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": { ...openedMixin(theme), boxShadow: "8px 0 24px rgba(16,24,40,.12)" },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

type NavItem = { label: string; to: string; icon: React.ReactNode; external?: boolean; onClick?: () => void };

export default function Header() {
  const userInfo = useAppSelector(getUserInfo);
  let defaultLic = LocalStorageProvider(userInfo.userId, null, LocalStorageMethod.getItem);
  if (userInfo.lics?.length >= 1 && defaultLic == "") {
    LocalStorageProvider(userInfo.userId, { lic: userInfo.lics[0].lic, address: "" }, LocalStorageMethod.setItem);
  }
  if (userInfo.lics?.length === 0) {
    LocalStorageProvider(userInfo.userId, null, LocalStorageMethod.removeItem);
    defaultLic = "";
  }
  if (defaultLic != "" && userInfo != undefined && userInfo?.lics.find((x) => x.lic == defaultLic.lic) == undefined) {
    defaultLic = "";
  }
  const hasLic = defaultLic != "" && defaultLic.lic != "";
  const { width } = useWindowDimensions();
  const isDesktop = width > 800;
  const [openChangeLick, setOpenCHangeLic] = React.useState(false);
  const [openPolicy, setOpenPolicy] = React.useState(false);
  const showPolicy = () => {
    // мобильные браузеры не показывают PDF внутри окна — там открываем файл в новой вкладке
    if ((navigator as any).pdfViewerEnabled === false) {
      window.open(POLICY_PDF_URL, "_blank", "noopener");
      return;
    }
    setOpenPolicy(true);
  };

  const dispath = useAppDispatch();
  const open = useAppSelector(getMenuState);
  const location = useLocation();
  useEffect(() => {
    if (defaultLic != "") {
      dispath(GetFullInfoByLic({ fullLic: defaultLic.lic }));
      dispath(GetUserLicAddresses());
      dispath(GetPeriodStatus());
    }
  }, [defaultLic.lic]);
  const address = useAppSelector(getPersonalInfo)?.address;
  const handleDrawerOpen = () => {
    dispath(showMenu(true));
  };
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const handeClick = (event: any) => {
    // нажатие внутри меню его не закрывает — иначе пункт уезжает из-под курсора и клик теряется;
    // меню закроется в onClick пункта после перехода
    if (drawerRef.current?.contains(event.target)) return;
    dispath(showMenu(false));
  };
  useEffect(() => {
    document.addEventListener("mousedown", handeClick);
    return () => document.removeEventListener("mousedown", handeClick);
  });
  const handleDrawerClose = () => {
    dispath(showMenu(false));
  };

  const mainItems: NavItem[] = [
    { label: "Главная", to: AppRoute.Main, icon: <HomeOutlinedIcon /> },
    { label: "Мои лицевые счета", to: AppRoute.Lic, icon: <BadgeOutlinedIcon /> },
    ...(hasLic
      ? [
          { label: "Передача показаний", to: AppRoute.SendReadings, icon: <WaterDropOutlinedIcon /> },
          { label: "Приборы учёта", to: AppRoute.Counter, icon: <SpeedOutlinedIcon /> },
          { label: "История оплат", to: AppRoute.HistoryPayment, icon: <PaymentsOutlinedIcon /> },
        ]
      : []),
    { label: "Мои обращения", to: AppRoute.UserRequests, icon: <ForumOutlinedIcon /> },
    { label: "Документы", to: AppRoute.Documents, icon: <FolderOutlinedIcon /> },
  ];
  const extraItems: NavItem[] = [
    { label: "Настройки аккаунта", to: AppRoute.Profile, icon: <ManageAccountsOutlinedIcon />, external: true },
    ...(userIsRole(userInfo, UserRole.Admin) || userIsRole(userInfo, UserRole.Operator)
      ? [{ label: "Админ панель", to: AppRoute.Admin, icon: <AdminPanelSettingsOutlinedIcon /> }]
      : []),
  ];

  const isActive = (to: string) => (to === AppRoute.Main ? location.pathname === "/" || location.pathname === to : location.pathname.startsWith(to));

  // Обёртка Tooltip есть всегда (при открытом меню — с пустым текстом). Если добавлять/убирать её
  // в зависимости от open, пункт пересоздаётся между mousedown (закрывает меню) и mouseup — и клик теряется.
  const renderItem = (item: NavItem) => (
    <Tooltip key={item.label} title={open ? "" : item.label} placement="right">
      <ListItemButton
        {...(item.onClick
          ? {}
          : { component: Link, to: item.to, target: item.external ? "_blank" : undefined, selected: !item.external && isActive(item.to) })}
        onClick={() => {
          handleDrawerClose();
          item.onClick?.();
        }}
        sx={{ minHeight: 46, px: 1.75, justifyContent: open ? "initial" : "center" }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : "auto", justifyContent: "center", color: brand.muted }}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: 500, fontSize: 15, lineHeight: 1.3, sx: { whiteSpace: open ? "normal" : "nowrap" } }}
          sx={{ opacity: open ? 1 : 0 }}
        />
      </ListItemButton>
    </Tooltip>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" color="inherit" sx={{ bgcolor: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 2, minHeight: { xs: 60, sm: 68 } }}>
          {isDesktop && (
            <Link to={AppRoute.Main} style={{ display: "flex", alignItems: "center", gap: 16 }} aria-label="На главную">
              <img src={logoRbr} alt="ООО Расчетно-биллинговые решения" style={{ height: 38 }} />
              <span style={{ width: 1, height: 34, backgroundColor: brand.line }} />
              <img src={logoEnergosbyt} alt="ЭнергосбыТ Плюс" style={{ height: 42 }} />
            </Link>
          )}

          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: isDesktop ? "center" : "flex-start", minWidth: 0 }}>
            {hasLic && (
              <Button
                onClick={() => setOpenCHangeLic(true)}
                color="inherit"
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  border: `1px solid ${brand.line}`,
                  bgcolor: alpha(brand.main, 0.04),
                  borderRadius: 12,
                  px: 2,
                  py: 0.75,
                  maxWidth: "100%",
                  textAlign: "left",
                  "&:hover": { bgcolor: alpha(brand.main, 0.1), borderColor: brand.main },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2, color: brand.ink }}>Л/с {defaultLic.lic}</Typography>
                  {address?.fullAddress && (
                    <Typography noWrap sx={{ fontSize: 12.5, color: brand.muted, lineHeight: 1.3, maxWidth: isDesktop ? 520 : "55vw" }}>
                      {address.fullAddress}
                    </Typography>
                  )}
                </Box>
              </Button>
            )}
          </Box>
          <LicDialog Open={openChangeLick} Close={setOpenCHangeLic} Title="Выбрать лицевой счёт" Component={<ChangeLick />} />

          {isDesktop && userInfo.userId && (
            <Typography sx={{ color: brand.muted, fontSize: 14, maxWidth: 220 }} noWrap title={userDisplayName(userInfo)}>
              {userDisplayName(userInfo)}
            </Typography>
          )}
          <Button
            color="inherit"
            onClick={() => {
              localStorage.removeItem("token");
              authService.signOut();
            }}
            startIcon={<LogoutIcon />}
            sx={{ color: brand.muted, flexShrink: 0, minWidth: 0, "&:hover": { color: brand.main } }}
            aria-label="Выйти"
          >
            {isDesktop ? "Выйти" : ""}
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" variant="permanent" open={open} onMouseOver={handleDrawerOpen} onMouseOut={handleDrawerClose} PaperProps={{ ref: drawerRef }}>
        <Toolbar sx={{ minHeight: { xs: 60, sm: 68 } }} />
        <List sx={{ pt: 1.5 }}>{mainItems.map(renderItem)}</List>
        <Divider sx={{ mx: 2 }} />
        <List>{extraItems.map(renderItem)}</List>
        <Box sx={{ flexGrow: 1 }} />
        <List sx={{ pb: 2 }}>
          {renderItem({ label: "Инструкция", to: "/Files/Инструкция.pdf?v=20260918", icon: <HelpOutlineIcon />, external: true })}
          {renderItem({ label: "Политика в отношении персональных данных", to: POLICY_PDF_URL, icon: <PrivacyTipOutlinedIcon />, onClick: showPolicy })}
        </List>
      </Drawer>
      <PdfViewerDialog open={openPolicy} onClose={() => setOpenPolicy(false)} title={POLICY_TITLE} src={POLICY_PDF_URL} downloadName={POLICY_DOWNLOAD_NAME} />
    </Box>
  );
}
